<template>
	<div data-tour-scope="highlighted-categories">
		<v-main>
			<v-container fluid fill-height>
				<v-layout justify-center align-center>
					<v-card width="100%" outlined elevation-0>
						<v-toolbar flat class="border_bottom" shaped>
							<v-toolbar-title> {{ $vuetify.lang.t('$vuetify.front.ecommerce.categoriesevidence') }}</v-toolbar-title>
							<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
							<v-spacer></v-spacer>
						</v-toolbar>


						<v-col cols="12">
							<v-row>
								<v-col cols="12" md="4" data-tour="search">
									<v-text-field v-model="searchfield" label="Cerca" outlined append-icon="mdi-magnify" dense @click:append="getSearch()" @keyup.enter="getSearch()" hide-details></v-text-field>
								</v-col>
								<v-col cols="12" md="8" data-tour="filter">
									<v-autocomplete
										hide-details="auto"
										outlined
										:value="categoriaFiltro"
										@change="filterCheck"
										:items="listcategories"
										label="Categorie"
										return-object
										item-value="_id"
										item-text="string"
										clearable
										dense
									>
									</v-autocomplete>
								</v-col>
							</v-row>
						</v-col>

						<v-data-table
							:headers="headers"
							:items="getCategories"
							:options.sync="settings.options"
							:server-items-length="total"
							must-sort
							class="elevation-0"
							:footer-props="{ 'items-per-page-options': listSize }"
							item-key="_id"
							data-tour="table"
							:hide-default-header="mobileCards"
							:class="{ 'table-cards': mobileCards }"
						>
							<template v-slot:body="{ items }">
								<!-- telefono: ogni riga diventa una card chiave/valore -->
								<tbody v-if="mobileCards" class="highlighted-cards">
									<tr v-for="item in items" :key="item._id">
										<td :colspan="headers.length" class="px-3 py-1">
											<key-value-card :title="item.trans ? item.trans.name : ''" :fields="campiCard(item)">
												<template v-slot:actions>
													<v-menu top eager>
														<template v-slot:activator="{ on, attrs }">
															<v-btn icon small color="primary" v-bind="attrs" v-on="on" data-tour="row-menu">
																<v-icon>mdi-dots-vertical</v-icon>
															</v-btn>
														</template>
														<v-list>
															<v-list-item link data-tour="row-edit" @click="editCategory(item)">
																<v-list-item-title class="size9">Modifica categoria</v-list-item-title>
															</v-list-item>
														</v-list>
													</v-menu>
												</template>
												<div v-if="item.id !== 0" class="d-flex align-center justify-space-between">
													<span class="card-label">In evidenza</span>
													<v-switch v-model="item.isFeatured" data-tour="switch" @change="changeStatus(item)" color="primary" class="mt-0 pt-0" hide-details></v-switch>
												</div>
											</key-value-card>
										</td>
									</tr>
									<tr v-if="!items.length">
										<td :colspan="headers.length" class="text-center grey--text py-6">Nessun elemento</td>
									</tr>
								</tbody>
								<tbody v-else>
									<tr v-for="item in items" :key="item._id">
										<td class="pl-0 pr-0 ml-0 mr-0" style="width: 5px">
											<v-menu top eager>
												<template v-slot:activator="{ on, attrs }">
													<v-btn icon dark v-bind="attrs" v-on="on" color="primary" dense data-tour="row-menu">
														<v-icon dark> mdi-dots-vertical </v-icon>
													</v-btn>
												</template>
												<v-list>
													<v-list-item link data-tour="row-edit" @click="editCategory(item)">
														<v-list-item-title class="size9">Modifica categoria</v-list-item-title>
													</v-list-item>
												</v-list>
											</v-menu>
										</td>
										<td>
											<v-switch v-if="item.id !== 0" v-model="item.isFeatured" data-tour="switch" @change="changeStatus(item)" color="primary" class="mt-0" hide-details></v-switch>
										</td>
										<td class="text-left">{{ item.created_at | moment('DD/MM/YYYY') }}</td>
										<td class="text-left">{{ item.id }}</td>
										<td class="text-left">{{ item.trans ? item.trans.name : '' }}</td>
										<td class="text-left">{{ item.references }}</td>
									</tr>
								</tbody>
							</template>
						</v-data-table>
					</v-card>
					<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" multi-line :timeout="snackbar.timeout">
						{{ snackbar.text }}
						<v-btn dark text @click="snackbar.snackbar = false"> Chiudi </v-btn>
					</v-snackbar>
				</v-layout>
			</v-container>
		</v-main>
		<!-- Ultimo elemento: il nodo viene spostato in `[data-app]` al montaggio. -->
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { Category } from '@/utils/Category';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import { useTour } from '@/components/core/tour/useTour';
import { createHighlightedCategoriesTourSteps, stopHighlightedCategoriesTour } from './highlighted-categories-tour';
import KeyValueCard from '@/components/core/KeyValueCard.vue';
import { listRouteState } from '@/components/core/list/listRouteState';
import { openWithReturn } from '@/components/core/pageReturn';

const LIST_SIZE = [15, 25, 50, 100];

export default {
	name: 'CategoriesHighlited',
	// pagina, righe, ordinamento, ricerca e categoria stanno nell'URL: refresh e ritorno ripartono da li'
	mixins: [listRouteState({ filters: { categories: 'numberArray' }, perPageOptions: LIST_SIZE })],
	setup() {
		return { tour: useTour(createHighlightedCategoriesTourSteps(), { onStop: stopHighlightedCategoriesTour }) };
	},
	components: {
		TourOverlay,
		KeyValueCard,
	},
	data() {
		return {
			snackbar: { snackbar: false, color: '', timeout: 5000, text: '' },
			settings: this.listSettingsFromRoute({
				search: '',
				filters: { categories: [] },
				options: { sortBy: ['created_at'], sortDesc: [true], page: 1, itemsPerPage: 15 },
			}),
			listSize: LIST_SIZE,
			searchfield: '',
			total: 0,
			headers: [
				// colonna del menu a tre puntini: non si ordina
				{ text: '', value: '', sortable: false },
				{ text: 'Categoria in evidenza', value: 'isFeatured' },
				{ text: 'Created', value: 'created_at' },
				{ text: 'Id', value: 'id' },
				{ text: 'Categoria', value: 'trans.name' },
				{ text: 'Reference', value: 'references' },
			],
			listcategories: [],
		};
	},
	async created() {
		this.listLoad();
		await this.fetchSetting();
		const langDefault = _.find(this.getSettings.languages, { default: true }).lang;
		const category = new Category(this.getSettings.categories, langDefault);
		await category.buildTotal();
		await category.refactorCategory();
		await category.createPath();
		await category.dynamicSort();
		this.listcategories = await category.getTree;
	},
	computed: {
		// sotto i 769px le righe diventano card
		mobileCards() {
			return this.$vuetify.breakpoint.width < 769;
		},
		// la categoria del filtro, ricostruita dall'id nell'URL
		categoriaFiltro() {
			const id = this.settings.filters.categories[0];
			if (id === undefined) return null;
			return _.find(this.listcategories, { id }) || null;
		},
		...mapGetters({
			getCategories: 'categories/getCategories',
			getSettings: 'settings/getSetting',
		}),
	},
	methods: {
		...mapMutations({
			setPreload: 'settings/setPreload',
		}),
		...mapActions({
			fetchItems: 'categories/asyncFetchData',
			setUpdateStatusCategoryFeatured: 'categories/setUpdateStatusCategoryFeatured',
			fetchSetting: 'settings/asyncFetchData',
		}),
		// coppie chiave/valore della card su telefono
		campiCard(item) {
			return [
				{ label: 'Id', value: item.id },
				{ label: 'Creata', value: item.created_at ? this.$moment(item.created_at).format('DD/MM/YYYY') : '' },
				{ label: 'Reference', value: Array.isArray(item.references) ? item.references.join(', ') : item.references },
			];
		},
		notifica(color, text) {
			this.snackbar = { snackbar: true, color, timeout: 5000, text };
		},
		// il mixin della lista riporta a pagina 1 e ricarica
		filterCheck(event) {
			this.settings.filters.categories = event ? [event.id] : [];
		},
		getSearch() {
			this.settings.search = _.size(this.searchfield) >= 3 ? this.searchfield : '';
		},
		// la categoria si modifica nella sua pagina; Annulla e Salva riportano qui, con la lista com'era
		editCategory(item) {
			openWithReturn(this, { path: `/categories/${item.id}` }, { label: 'Torna alle categorie in evidenza' });
		},
		changeStatus(item) {
			this.setPreload(true);
			this.setUpdateStatusCategoryFeatured({ _id: item._id, isFeatured: item.isFeatured })
				.then(result => {
					if (result.data.data.ok) this.notifica('success', 'Update successfully');
				})
				.catch(err => {
					console.log(err);
					this.notifica('error', err.message);
				})
				.finally(() => this.setPreload(false));
		},
		getDataFromApi() {
			this.setPreload(true);
			return this.fetchItems(this.settings)
				.then(result => {
					this.total = result.total;
				})
				.catch(err => console.log(err))
				.finally(() => this.setPreload(false));
		},
	},
	beforeDestroy() {
		this.tour.stop();
	},
};
</script>

<style scoped>
/* vista card: niente layout a colonne, ogni riga e' un blocco e la card prende il 100% */
.table-cards ::v-deep table {
	display: block;
	width: 100%;
}
.highlighted-cards,
.highlighted-cards tr {
	display: block;
	width: 100%;
}
.highlighted-cards tr,
.highlighted-cards tr:hover {
	background: transparent !important;
}
.highlighted-cards td {
	display: block;
	width: 100%;
	border-bottom: none !important;
	height: auto !important;
}
/* respiro tra le card e il bordo della card che contiene la tabella */
.highlighted-cards tr:first-child td {
	padding-top: 12px !important;
}
.highlighted-cards tr:last-child td {
	padding-bottom: 12px !important;
}
.highlighted-cards tr + tr td {
	padding-top: 8px !important;
}
.card-label {
	font-size: 14px;
	color: rgba(0, 0, 0, 0.6);
}
</style>
